import {
  Box,
  IconButton,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import useIsOnline from 'hooks/useIsOnline';
import { useTranslation } from 'next-i18next';
import type { FC } from 'react';

import DeviceIconComponent from 'components/DeviceIconComponent';
import CustomTrashIcon from 'components/Icons/CustomTrashIcon';
import OrganizationsIcon from 'components/Icons/OrganizationsIcon/intex';

import type { PairedDevicesProps } from 'redux-store/slices/rfids/types';

export interface UnPairDeviceListProps {
  device: PairedDevicesProps & { name?: string };
  isLoading: boolean;
  handleUnPairDeviceFromRfid: (member: number) => void;
}

const CustomBox = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.vars.palette.grey[100]}`,
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  gap: theme.spacing(1),
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  cursor: 'pointer',
}));

const UnPairDeviceList: FC<UnPairDeviceListProps> = ({
  device,
  isLoading,
  handleUnPairDeviceFromRfid,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const online = useIsOnline(device.serial);

  return (
    <CustomBox>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Box
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          gap={1}
        >
          <Box display="flex" flexDirection="row" gap={1}>
            <DeviceIconComponent
              typ={device.type}
              subtype={device.subtype}
              outlined
            />
            <Box display="flex" flexDirection="column">
              <Typography data-testid="device-title">
                {device.name ??
                  t('common:serial_number_with_value', {
                    value: device.serial,
                  })}
              </Typography>
              {device.name ? (
                <Typography variant="subtitle1" data-testid="device-serial">
                  {t('common:serial_number_with_value', {
                    value: device.serial,
                  })}
                </Typography>
              ) : !online ? (
                <Typography variant="subtitle1" data-testid="device-is-offline">
                  {t(
                    'common:pages.device_groups.add_device_group.device_is_offline',
                  )}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Box>
        <Box
          gap={1}
          maxWidth={200}
          flexDirection="row"
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
        >
          {device.organizationNames.length > 0 && !smallScreen ? (
            <Box
              gap={1}
              maxWidth={200}
              flexDirection="row"
              display="flex"
              alignItems="center"
            >
              <OrganizationsIcon fontSize="small" sx={{ color: 'grey.500' }} />
              <Typography
                data-testid="device-group-names"
                variant="body1"
                noWrap
                color={theme.vars.palette.grey[500]}
              >
                {device.organizationNames.map((org, index) => (
                  <span key={org}>
                    {org}
                    {index < device.organizationNames.length - 1 ? ', ' : null}
                  </span>
                ))}
              </Typography>
            </Box>
          ) : null}
          {device.canUnpair ? (
            <IconButton
              disabled={isLoading}
              onClick={() => handleUnPairDeviceFromRfid(device.deviceId)}
            >
              <CustomTrashIcon color="error" />
            </IconButton>
          ) : null}
        </Box>
      </Box>
    </CustomBox>
  );
};

export default UnPairDeviceList;
